// Captures the emailed set-password link before the router starts (vue-vxapp#364).
//
// The link is `/set-password#token=<43 base64url characters>&tenant=<API GUID>`. A URL fragment is
// never sent to a server, but the router, the scroll behaviour and the route logger would all read
// it. This module runs first in `main.js`: it moves the token into module memory and removes the
// fragment from the address bar and history before any of them can see it. The token is never
// written to a store, cookie, storage or log, and `takePasswordLink()` hands it out once.

export interface PasswordLink {
  token: string
  tenant: string
}

export const SET_PASSWORD_PATH = '/set-password'

const TOKEN_PATTERN = /^[\w-]{43}$/
const TENANT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

let pendingLink: PasswordLink | null = null

const setPasswordPathname = (): string =>
  `${import.meta.env.BASE_URL.replace(/\/+$/, '')}${SET_PASSWORD_PATH}`

const parseLink = (fragment: string): PasswordLink | null => {
  const params = new URLSearchParams(fragment.replace(/^#/, ''))
  const tokens = params.getAll('token')
  const tenants = params.getAll('tenant')

  if (tokens.length !== 1 || tenants.length !== 1) return null
  if (!TOKEN_PATTERN.test(tokens[0]) || !TENANT_PATTERN.test(tenants[0])) return null

  return { token: tokens[0], tenant: tenants[0] }
}

/**
 * On the set-password path, keeps a well-formed link in memory and rewrites the current history
 * entry to the bare path. Any fragment or query on that path is dropped, including a malformed
 * one, so no part of a token stays in the address bar. Other paths are left untouched.
 */
export const capturePasswordLinkFragment = (
  location: Pick<Location, 'pathname' | 'hash' | 'search'> = window.location,
  history: Pick<History, 'state' | 'replaceState'> = window.history
): void => {
  if (location.pathname.replace(/\/+$/, '') !== setPasswordPathname()) return

  pendingLink = parseLink(location.hash)

  if (location.hash || location.search) {
    history.replaceState(history.state, '', location.pathname)
  }
}

/** Returns the captured link once, then forgets it. */
export const takePasswordLink = (): PasswordLink | null => {
  const link = pendingLink

  pendingLink = null

  return link
}

/** Forgets any captured link. */
export const clearPasswordLink = (): void => {
  pendingLink = null
}
